/**
 * Goals tab — UX/UI Specification §9.9.
 *
 * Two kinds of goal. One is tracked in cm or kg and shows a percentage; the
 * other cannot be, and carries the instructor's own note instead. Nothing in
 * between: an earlier version gave the second kind a fixed status badge, and
 * it read as a verdict the studio had not actually made.
 *
 * There is no limit on how many goals a student may have.
 */

import { useMemo, useState } from 'react';
import { formatDateLong, formatPercent, formatValue, type GoalType } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import type { MeasurementListResponse } from '../api/types.js';
import { GoalForm, type GoalFormValue } from '../components/GoalForm.js';
import { messageFor } from '../api/error-messages.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { useToast } from '../components/ui/ToastContext.js';
import { useApiData } from '../hooks/useApiData.js';
import { toRecord } from './MeasurementsTab.js';
import './GoalsTab.css';

interface Goal {
  id: string;
  goalType: GoalType;
  /** Set only when goalType is 'custom_metric'. */
  customMetricLabel: string | null;
  title: string;
  goalMode: 'numeric' | 'qualitative';
  startValue: number | null;
  currentValue: number | null;
  targetValue: number | null;
  unit: string | null;
  targetDate: string | null;
  qualitativeNote: string | null;
  progressPct: number | null;
  status: 'active' | 'completed';
}

export function GoalsTab({ studentId }: { studentId: string }) {
  const { data, error, loading, reload } = useApiData<{ items: Goal[] }>(
    `/api/students/${studentId}/goals`,
  );
  // GoalForm needs the student's full measurement history to resolve a
  // tracked value and to list the student's own custom fields.
  const { data: measurementData } = useApiData<MeasurementListResponse>(
    `/api/students/${studentId}/measurements`,
  );
  const toast = useToast();

  const [adding, setAdding] = useState(false);
  /** Set while editing an existing goal; null while adding or idle. */
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Goal | null>(null);

  const items = data?.items ?? [];
  const active = items.filter((g) => g.status === 'active');
  const completed = items.filter((g) => g.status === 'completed');

  const records = useMemo(
    () => (measurementData?.items ?? []).map(toRecord),
    [measurementData],
  );

  async function act(goal: Goal, action: 'complete' | 'reopen' | 'cancel') {
    try {
      await api.post(`/api/students/${studentId}/goals/${goal.id}/${action}`);
      reload();
    } catch (err) {
      toast.show(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /**
   * Completing offers an undo rather than a confirmation: nothing is lost, and
   * a prompt on a harmless action teaches people to dismiss prompts unread.
   */
  async function complete(goal: Goal) {
    await act(goal, 'complete');
    toast.show(`"${goal.title}" tamamlandı olarak işaretlendi.`, {
      label: 'Geri al',
      onAction: () => void act(goal, 'reopen'),
    });
  }

  async function remove(goal: Goal) {
    setConfirmRemove(null);
    await act(goal, 'cancel');
    toast.show(`"${goal.title}" kaldırıldı.`, {
      label: 'Geri al',
      onAction: () => void act(goal, 'reopen'),
    });
  }

  function handleSaved(_saved: GoalFormValue) {
    setAdding(false);
    setEditingGoal(null);
    reload();
  }

  function renderGoal(goal: Goal) {
    return (
      <li key={goal.id} className="goal">
        <div className="goal__head">
          <span className="goal__title">{goal.title}</span>
          <div className="goal__actions">
            <button type="button" onClick={() => setEditingGoal(goal)}>
              Düzenle
            </button>
            {goal.status === 'active' ? (
              <button type="button" onClick={() => void complete(goal)}>
                Tamamla
              </button>
            ) : (
              <button type="button" onClick={() => void act(goal, 'reopen')}>
                Yeniden aç
              </button>
            )}
            <button
              type="button"
              className="goal__cancel"
              onClick={() => setConfirmRemove(goal)}
            >
              Kaldır
            </button>
          </div>
        </div>

        {goal.goalMode === 'numeric' ? (
          <>
            <div className="goal__values">
              <span>
                <em>Başlangıç</em>{' '}
                {goal.startValue === null
                  ? '—'
                  : formatValue(goal.startValue, goal.unit ?? '', 1)}
              </span>
              <span>
                <em>Güncel</em>{' '}
                {goal.currentValue === null
                  ? '—'
                  : formatValue(goal.currentValue, goal.unit ?? '', 1)}
              </span>
              <span>
                <em>Hedef</em>{' '}
                {goal.targetValue === null
                  ? '—'
                  : formatValue(goal.targetValue, goal.unit ?? '', 1)}
              </span>
            </div>

            {/* No bar and no percentage when it is not computable — a goal
                missing a value shows the values it has (Doc 6 §14.2). */}
            {goal.progressPct !== null && (
              <div className="goal__progress">
                <div className="goal__bar">
                  <div
                    className="goal__bar-fill"
                    style={{ width: `${goal.progressPct}%` }}
                  />
                </div>
                <span className="goal__pct">{formatPercent(goal.progressPct)}</span>
              </div>
            )}
          </>
        ) : (
          goal.qualitativeNote && <p className="goal__note">{goal.qualitativeNote}</p>
        )}

        {goal.targetDate && (
          <span className="goal__date">
            Hedef tarih {formatDateLong(goal.targetDate)}
          </span>
        )}
      </li>
    );
  }

  return (
    <div className="goals">
      <div className="goals__head">
        <h2 className="profile__section">Hedefler</h2>
        {!adding && !editingGoal && (
          <Button type="button" onClick={() => setAdding(true)}>
            Hedef Ekle
          </Button>
        )}
      </div>

      {error && <p role="alert">{error}</p>}

      {(adding || editingGoal) && (
        <GoalForm
          studentId={studentId}
          records={records}
          initial={editingGoal}
          onCancel={() => {
            setAdding(false);
            setEditingGoal(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !adding && (
        <div className="profile__empty">
          <p>Henüz hedef tanımlanmamış.</p>
          <Button type="button" onClick={() => setAdding(true)}>
            Hedef Ekle
          </Button>
        </div>
      )}

      {active.length > 0 && (
        <>
          <h3 className="goals__group">Aktif Hedefler</h3>
          <ul className="goals__list">{active.map(renderGoal)}</ul>
        </>
      )}

      {completed.length > 0 && (
        <>
          <h3 className="goals__group">Tamamlanan Hedefler</h3>
          <ul className="goals__list">{completed.map(renderGoal)}</ul>
        </>
      )}

      {confirmRemove && (
        <ConfirmDialog
          title="Hedef kaldırılsın mı?"
          body={`"${confirmRemove.title}" listeden kaldırılacak. Bu işlemi geri alabilirsiniz.`}
          confirmLabel="Kaldır"
          onCancel={() => setConfirmRemove(null)}
          onConfirm={() => void remove(confirmRemove)}
        />
      )}
    </div>
  );
}
